/**
 * A class that manages mappings between source and target objects.
 *
 * This class allows defining and applying mappings using a specified strategy.
 *
 * @class
 * @property {Map<string, (source: any, target: any) => void>} mappings - A map storing the mapping functions.
 *
 * @method addMapping
 * @param {string} key - The key identifying the mapping.
 * @param {{ [key: string]: string | ((sourceValue: any, target: any) => void) }} fieldMappings - An object defining field mappings between source and target.
 * @param {string} [strategyName='default'] - The name of the mapping strategy.
 * @returns {void}
 *
 * @method map
 * @template T, K
 * @param {T} source - The source object to map from.
 * @param {string} key - The key identifying the mapping.
 * @param {string} [strategyName='default'] - The name of the mapping strategy.
 * @returns {K} - The target object after applying the mapping.
 * @throws {Error} - Throws an error if no mapping is found for the specified key and strategy.
 */

class Mapper {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    private mappings: Map<string, (source: any, target: any) => void> = new Map();

    // Define a mapping between source and target objects
    public addMapping(
        key: string,
        /* eslint-disable @typescript-eslint/no-explicit-any */
        fieldMappings: { [key: string]: string | ((sourceValue: any, target: any) => void) },
        strategyName: string = 'default',
    ): void {
        const mapKey = `${key}::${strategyName}`;

        // tslint:disable:no-any
        const mappingFunction = (source: any, target: any) => {
            Object.keys(fieldMappings).forEach(sourceKey => {
                const targetKeyOrFn = fieldMappings[sourceKey];

                if (typeof targetKeyOrFn === 'string') {
                    const targetKey = targetKeyOrFn;

                    // Only set the field if it is not already defined
                    if (target[targetKey] === undefined) {
                        target[targetKey] = source[sourceKey];
                    }
                } else if (typeof targetKeyOrFn === 'function') {
                    targetKeyOrFn(source[sourceKey], target);
                }
            });
        };

        this.mappings.set(mapKey, mappingFunction);
    }

    // Apply a mapping strategy
    public map<T, K>(source: T, key: string, strategyName: string = 'default'): K {
        const mapKey = `${key}::${strategyName}`;
        const mappingFunction = this.mappings.get(mapKey);

        if (!mappingFunction) {
            throw new Error(`No mapping found for ${mapKey}`);
        }

        const target: K = {} as K;
        mappingFunction(source, target);
        return target;
    }
}

export { Mapper };